/**
 * ModerationPage — Supabase-native HITL moderation interface.
 * All data comes from Supabase contacts and messages tables.
 * No Telerivet dependencies.
 */

import { useState, useEffect, useRef, useCallback } from "react"
import { Users, PanelLeftOpen, FileText } from "lucide-react"
import { Button } from "@/components/ui/button"
import { usePage } from "@/components/page/hooks"
import { useConversations } from "./hooks"
import { ContactList, ConversationView, FormPanel } from "./components"
import { ModerationContact } from "./types"
import { supabase } from "@/lib/data"

export default function ModerationPage() {
  const { team } = usePage()

  const [selectedContact, setSelectedContact]   = useState<ModerationContact | null>(null)
  // Agent that handled the selected conversation; scopes the Forms panel to that
  // agent's bound forms (plus records the contact already filled).
  const [conversationAgentId, setConversationAgentId] = useState<number | null>(null)
  // Whether ConversationView has reported the handling agent for the *current*
  // contact yet. Until it has, the derived agent is unknown — not "no agent" —
  // so we must not fall back to showing every team agent's forms.
  const [agentResolved, setAgentResolved] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [formPanelOpen, setFormPanelOpen]       = useState(true)
  const [currentUserId, setCurrentUserId]       = useState<string | null>(null)
  const [currentUserOperatorContactId, setCurrentUserOperatorContactId] = useState<string | undefined>(undefined)
  const [availableUsers, setAvailableUsers]     = useState<Array<{ id: string; email: string; name?: string }>>([])

  const syncContactInUrl = useCallback((contactId: string | null, mode: "replace" | "push" = "replace") => {
    const params = new URLSearchParams(window.location.search)

    if (contactId) {
      params.set("contact", contactId)
    } else {
      params.delete("contact")
    }

    const nextUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname
    const currentUrl = `${window.location.pathname}${window.location.search}`
    if (nextUrl === currentUrl) return

    if (mode === "push") {
      window.history.pushState({}, "", nextUrl)
      return
    }
    window.history.replaceState({}, "", nextUrl)
  }, [])

  const {
    conversations,
    loading,
    error,
    filter,
    sourceFilter,
    searchQuery,
    setFilter,
    setSourceFilter,
    setSearchQuery,
    refresh,
    updateContactLocally,
    updateContactFlags,
    hasMoreMessages,
    loadingMoreMessages,
    loadMoreMessages,
    searching,
  } = useConversations()

  // Get current auth user + fetch team operators for assignment
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) setCurrentUserId(data.user.id)
    })
  }, [])

  // Deep-link source-filter fix: the source filter defaults to "real_only", so a
  // ?contact= pointing at a synthetic conversation (e.g. opened from the
  // Simulator's "Back to moderation") would be filtered out of the list and the
  // page would fall back to selecting some other contact. If the deep-linked
  // contact is synthetic, widen the source filter so it's visible. Runs once.
  const didApplyDeepLinkSourceFilterRef = useRef(false)
  useEffect(() => {
    if (didApplyDeepLinkSourceFilterRef.current) return
    const targetId = new URLSearchParams(window.location.search).get("contact")
    if (!targetId) { didApplyDeepLinkSourceFilterRef.current = true; return }
    didApplyDeepLinkSourceFilterRef.current = true
    supabase
      .from("contacts")
      .select("type")
      .eq("id", targetId)
      .single()
      .then(({ data }) => {
        if ((data as any)?.type === "synthetic") setSourceFilter("all")
      })
  }, [setSourceFilter])

  useEffect(() => {
    if (!team?.id) return
    // Fetch all members of this team via user_teams → users
    supabase
      .from("user_teams")
      .select("users!user_teams_user_id_fkey(id, email, first_name, last_name)")
      .eq("team_id", team.id)
      .then(({ data, error }) => {
        if (error) { console.error("[ModerationPage] failed to fetch team members:", error); return }
        if (data) {
          const users = data
            .map((row: any) => row.users)
            .filter(Boolean)
            .map((u: any) => ({
              id: u.id as string,
              email: (u.email ?? "") as string,
              name: [u.first_name, u.last_name].filter(Boolean).join(" ") || undefined,
            }))
          setAvailableUsers(users)
        }
      })
  }, [team?.id])

  // Resolve the current user's operator contact id for HITL sender attribution.
  useEffect(() => {
    if (!team?.id || !currentUserId || availableUsers.length === 0) return
    let cancelled = false

    const normalize = (value: string | undefined | null) => (value ?? "").trim().toLowerCase()

    const resolveOperatorContact = async () => {
      const { data, error } = await supabase
        .from("contacts")
        .select("id, name")
        .eq("team", team.id)
        .eq("type", "operator")

      if (cancelled) return
      if (error) {
        console.error("[ModerationPage] failed to fetch operator contacts:", error)
        setCurrentUserOperatorContactId(undefined)
        return
      }

      const currentUser = availableUsers.find(u => u.id === currentUserId)
      const userName = normalize(currentUser?.name)
      const userEmail = normalize(currentUser?.email)

      const rows = (data ?? []) as Array<{ id?: string; name?: string | null }>
      const matched = rows.find(row => {
        const operatorName = normalize(row.name)
        if (!operatorName) return false
        return (userName && operatorName === userName) || (userEmail && operatorName === userEmail)
      })
      if (matched?.id) {
        setCurrentUserOperatorContactId(matched.id)
        return
      }

      // Auto-provision operator contact from current user profile if missing.
      const operatorContactId = `operator:${currentUserId}`
      const operatorName = currentUser?.name || currentUser?.email || "Operator"
      const operatorData = JSON.stringify({
        auth_user_id: currentUserId,
        email: currentUser?.email ?? null,
      })

      const { error: insertError } = await supabase
        .from("contacts")
        .upsert({
          id: operatorContactId,
          name: operatorName,
          type: "operator",
          team: team.id,
          data: operatorData,
        })

      if (insertError) {
        console.error("[ModerationPage] failed to create operator contact:", insertError)
        setCurrentUserOperatorContactId(undefined)
        return
      }

      setCurrentUserOperatorContactId(operatorContactId)
    }

    void resolveOperatorContact()
    return () => { cancelled = true }
  }, [team?.id, currentUserId, availableUsers])

  // Auto-select first conversation or handle ?contact= param.
  // Only re-run when the list goes from empty→populated (length crosses 0) or
  // when selectedContact is cleared — not on every 10s poll.
  const conversationsLengthRef = useRef(0)
  const conversationsRef = useRef(conversations)
  conversationsRef.current = conversations

  useEffect(() => {
    const wasEmpty = conversationsLengthRef.current === 0
    conversationsLengthRef.current = conversations.length
    if (conversations.length === 0 || (!wasEmpty && selectedContact)) return

    const params = new URLSearchParams(window.location.search)
    const targetId = params.get("contact")
    if (targetId) {
      const target = conversations.find(c => c.contact.id === targetId)
      if (target) {
        setSelectedContact(target.contact)
        return
      }
    }
    if (!selectedContact) {
      const first = conversations[0]?.contact ?? null
      setSelectedContact(first)
      syncContactInUrl(first?.id ?? null)
    }
  }, [conversations.length, selectedContact, conversations, syncContactInUrl])

  // Honor a ?contact= deep-link once it appears in the list. The auto-select
  // above may have fallen back to the first conversation while the target was
  // still filtered out (e.g. a synthetic contact before the source filter
  // widened). When the target becomes visible, select it — once.
  const honoredDeepLinkRef = useRef(false)
  useEffect(() => {
    if (honoredDeepLinkRef.current) return
    const targetId = new URLSearchParams(window.location.search).get("contact")
    if (!targetId) { honoredDeepLinkRef.current = true; return }
    if (selectedContact?.id === targetId) { honoredDeepLinkRef.current = true; return }
    const target = conversations.find(c => c.contact.id === targetId)
    if (target) {
      honoredDeepLinkRef.current = true
      setSelectedContact(target.contact)
      syncContactInUrl(target.contact.id)
    }
  }, [conversations, selectedContact, syncContactInUrl])

  // If a source/filter change removes the selected contact from the sidebar list,
  // switch to the first visible conversation (or clear selection if none remain).
  useEffect(() => {
    if (!selectedContact) return
    const stillVisible = conversations.some(c => c.contact.id === selectedContact.id)
    if (stillVisible) return
    const next = conversations[0]?.contact ?? null
    setSelectedContact(next)
    syncContactInUrl(next?.id ?? null)
  }, [conversations, selectedContact, syncContactInUrl])

  // Keep selected contact in sync with live conversation updates.
  // Run only when the specific fields that matter for the selected contact
  // actually change — not on every poll that touches any contact.
  const selectedContactRef = useRef<ModerationContact | null>(null)
  selectedContactRef.current = selectedContact

  // Clear the scoped agent when switching conversations; ConversationView reports
  // the new one once its messages load. Mark it unresolved until that report
  // arrives so the Forms panel shows a loading state instead of every team form.
  useEffect(() => {
    setConversationAgentId(null)
    setAgentResolved(false)
  }, [selectedContact?.id])

  const selectedConv = conversations.find(c => c.contact.id === selectedContact?.id)
  const moderationDataStr = (c: { moderation_data?: unknown }) =>
    (typeof c.moderation_data === "string" ? c.moderation_data : "")

  const syncKey = selectedConv
    ? `${selectedConv.contact.no_reply_needed}|${selectedConv.contact.severity}|${selectedConv.contact.summary}|${moderationDataStr(selectedConv.contact)}|${selectedConv.contact.hitl === true ? "1" : "0"}`
    : null

  useEffect(() => {
    const current = selectedContactRef.current
    if (!current || !syncKey) return
    const updated = conversationsRef.current.find(c => c.contact.id === current.id)
    if (!updated) return
    const changed =
      updated.contact.no_reply_needed !== current.no_reply_needed ||
      updated.contact.severity !== current.severity ||
      updated.contact.summary !== current.summary ||
      moderationDataStr(updated.contact) !== moderationDataStr(current) ||
      (updated.contact.hitl === true) !== (current.hitl === true)
    if (changed) setSelectedContact(updated.contact)
  }, [syncKey])

  const handleSelectContact = (contactId: string) => {
    const conv = conversations.find(c => c.contact.id === contactId)
    if (!conv) return
    setSelectedContact(conv.contact)
    syncContactInUrl(conv.contact.id, "push")
  }

  const handleContactUpdated = (contactId: string, updates: Partial<ModerationContact>) => {
    updateContactLocally(contactId, updates)
    if (selectedContact?.id === contactId) {
      setSelectedContact(prev => prev ? { ...prev, ...updates } : prev)
    }
  }

  return (
    <div className="flex flex-col h-full p-3 gap-2">
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</div>
      )}

      <div className="flex flex-1 gap-2 min-h-0">
        {!sidebarCollapsed && (
          <div className="w-[320px] shrink-0 flex flex-col min-h-0">
            <ContactList
              conversations={conversations}
              loading={loading}
              selectedContactId={selectedContact?.id || null}
              onSelectContact={handleSelectContact}
              filter={filter}
              onFilterChange={setFilter}
              sourceFilter={sourceFilter}
              onSourceFilterChange={setSourceFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onCollapse={() => setSidebarCollapsed(true)}
              hasMoreMessages={hasMoreMessages}
              loadingMoreMessages={loadingMoreMessages}
              onLoadMoreMessages={loadMoreMessages}
              searching={searching}
            />
          </div>
        )}

        {sidebarCollapsed && (
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0 self-start mt-1"
            onClick={() => setSidebarCollapsed(false)}
            title="Show contacts"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </Button>
        )}

        <div className="flex-1 min-w-0 min-h-0">
          {selectedContact ? (
            <ConversationView
              contact={selectedContact}
              availableUsers={availableUsers}
              currentUserId={currentUserId || undefined}
              currentUserOperatorContactId={currentUserOperatorContactId}
              onUpdateFlags={updateContactFlags}
              onContactUpdated={handleContactUpdated}
              onAgentIdChange={(cid, aid) => {
                if (cid === selectedContact.id) {
                  setConversationAgentId(aid)
                  setAgentResolved(true)
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No conversation selected</p>
                <p className="text-sm">Select a conversation from the list to begin</p>
              </div>
            </div>
          )}
        </div>

        {selectedContact && formPanelOpen && (
          <div className="w-[340px] shrink-0 flex flex-col relative z-10 bg-background min-w-0 min-h-0">
            <FormPanel
              contactId={selectedContact.id}
              teamId={team?.id}
              agentId={conversationAgentId}
              resolving={!agentResolved}
              actorId={currentUserOperatorContactId || currentUserId || undefined}
              onCollapse={() => setFormPanelOpen(false)}
              pollMs={10000}
            />
          </div>
        )}

        {selectedContact && !formPanelOpen && (
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0 self-start mt-1"
            onClick={() => setFormPanelOpen(true)}
            title="Show forms"
          >
            <FileText className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
